import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, Weight, User, Truck } from 'lucide-react';
import { TransportJob } from '@/hooks/useTransportJobs';
import { useTranslation } from 'react-i18next';

interface MissionDetailsDialogProps {
  job: TransportJob | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssign: () => void;
}

const getStatusBadge = (job: TransportJob, t: any) => {
  if (job.completed_at) {
    return <Badge className="bg-[#14A38B] text-white">{t('missions.card.status.completed')}</Badge>;
  }
  if (job.started_at) {
    return <Badge className="bg-[#0880AE] text-white">{t('missions.card.status.inProgress')}</Badge>;
  }
  if (job.assigned_at || (job.driver_id && job.vehicle_id)) {
    return <Badge className="bg-[#37699E] text-white">{t('missions.card.status.assigned')}</Badge>;
  }
  return <Badge className="bg-[#F2AC57] text-white">{t('missions.card.status.pending')}</Badge>;
};

export const MissionDetailsDialog = ({
  job,
  open,
  onOpenChange,
  onAssign,
}: MissionDetailsDialogProps) => {
  const { t, i18n } = useTranslation();

  if (!job) return null;

  const order = job.orders;
  const canAssign = !job.driver_id && !job.vehicle_id && !job.started_at && !job.completed_at;

  const getLocalizedDate = (dateString: string) => {
    const locale = i18n.language === 'en' ? 'en-US' : 
                   i18n.language === 'es' ? 'es-ES' :
                   i18n.language === 'de' ? 'de-DE' :
                   i18n.language === 'it' ? 'it-IT' : 'fr-FR';
    
    return new Date(dateString).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle className="text-2xl font-medium text-[#37699E] font-heading">
              {order?.cargo_title || order?.cargo_description}
            </DialogTitle>
            {getStatusBadge(job, t)}
          </div>
          <p className="text-sm text-[#5A6B7A]">{t('missions.card.order')} {order?.order_number}</p>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Route & Dates */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-[#37699E] font-heading">{t('missions.dialog.route')}</h3>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <MapPin size={16} className="text-[#0880AE] flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-[#2D3748] font-medium">
                    {order?.pickup_city}, {order?.pickup_country}
                  </p>
                  <p className="text-xs text-[#5A6B7A]">{order?.pickup_address}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 pl-6">
                <Calendar size={14} className="text-[#5A6B7A]" />
                <p className="text-xs text-[#5A6B7A]">
                  {order?.pickup_date && getLocalizedDate(order.pickup_date)}
                </p>
              </div>

              <div className="flex items-start gap-2 mt-3">
                <MapPin size={16} className="text-[#14A38B] flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-[#2D3748] font-medium">
                    {order?.delivery_city}, {order?.delivery_country}
                  </p>
                  <p className="text-xs text-[#5A6B7A]">{order?.delivery_address}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 pl-6">
                <Calendar size={14} className="text-[#5A6B7A]" />
                <p className="text-xs text-[#5A6B7A]">
                  {order?.delivery_date && getLocalizedDate(order.delivery_date)}
                </p>
              </div>
            </div>
          </div>

          {/* Cargo Details */}
          <div className="space-y-4 border-t border-[#DBE2EA] pt-4">
            <h3 className="text-lg font-medium text-[#37699E] font-heading">{t('missions.dialog.cargo')}</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <Weight size={16} className="text-[#5A6B7A]" />
                <div>
                  <p className="text-xs text-[#5A6B7A]">{t('missions.card.weight')}</p>
                  <p className="text-sm font-medium text-[#2D3748]">{order?.weight_kg} kg</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-[#5A6B7A]">{t('missions.card.type')}</p>
                <p className="text-sm font-medium text-[#2D3748]">{order?.trailer_type}</p>
              </div>
              <div>
                <p className="text-xs text-[#5A6B7A]">{t('missions.card.price')}</p>
                <p className="text-sm font-medium text-[#37699E]">
                  {order?.final_price || order?.proposed_price} €
                </p>
              </div>
            </div>
          </div>

          {/* Assignment Info */}
          {(job.driver_id || job.vehicle_id) && (
            <div className="space-y-4 border-t border-[#DBE2EA] pt-4">
              <h3 className="text-lg font-medium text-[#37699E] font-heading">{t('missions.dialog.assignment')}</h3>
              <div className="space-y-2">
                {job.drivers && (
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-[#0880AE]" />
                    <p className="text-sm text-[#2D3748]">
                      <span className="font-medium">{t('missions.card.driver')}</span>{' '}
                      {job.drivers.users?.first_name} {job.drivers.users?.last_name}
                    </p>
                  </div>
                )}
                {job.vehicles && (
                  <div className="flex items-center gap-2">
                    <Truck size={16} className="text-[#0880AE]" />
                    <p className="text-sm text-[#2D3748]">
                      <span className="font-medium">{t('missions.card.vehicle')}</span>{' '}
                      {job.vehicles.plate_number} - {job.vehicles.brand} {job.vehicles.model}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          {canAssign && (
            <div className="border-t border-[#DBE2EA] pt-4">
              <Button
                onClick={() => {
                  onAssign();
                  onOpenChange(false);
                }}
                className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg"
              >
                {t('missions.card.assign')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
